import React, { useMemo } from 'react'
import { InputFieldBase } from '../types'
import { CheckboxProps } from '../Checkbox/Checkbox'
import { useController, useWatch } from 'react-hook-form'

import FormControl from '@mui/material/FormControl'
import Box from '@mui/material/Box'
import FormLabel from '@mui/material/FormLabel'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import Checkbox from '@mui/material/Checkbox'
import PopOverInfo from '../../PopOverInfo'
import ErrorMessage from '../../ErrorMessage'

export type CheckboxGroupProps = InputFieldBase & {
  checkboxes: CheckboxProps[]
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  fieldType,
  label,
  checkboxes,
  popover,
  assistiveText,
  control,
  required,
  disabled,
  ...props
}) => {
  const fieldName = props.name!
  const { field, fieldState } = useController<[], string>({
    name: fieldName,
    control,
    rules: { required: required! },
    defaultValue: [],
  })
  const checkboxIds = useWatch({ control, name: fieldName }) || []
  const hasError = Boolean(fieldState.error)

  const handleChange = (value: string) => {
    const newArray = [...checkboxIds]
    const item = value

    //Ensure array isnt empty
    if (newArray.length > 0) {
      //Attempt to find an item in array with matching id
      const index = newArray.findIndex(x => x === item)

      // If theres no match add item to the array
      if (index === -1) {
        newArray.push(item)
      } else {
        //If there is a match and the value is empty, remove the item from the array
        newArray.splice(index, 1)
      }
    } else {
      //If the array is empty, add the item to the array
      newArray.push(item)
    }

    //Overwrite existing array with newArray}
    field.onChange(newArray)
  }

  const styles = useMemo(
    () => ({
      formControlLabel: {
        marginLeft: '-8px',
        '& > .MuiCheckbox-root': {
          alignSelf: 'baseline',
        },
        '& > .MuiFormControlLabel-label': {
          margin: '8px 0px 14px 0px',
          textAlign: 'start',
        },
      },
      root: {
        paddingBottom: '24px',
      },
      errorLabel: {
        margin: '8px 0 0 0',
        paddingBottom: '24px',
      },
    }),
    [],
  )

  return (
    <div>
      <FormControl component="fieldset" sx={styles.root}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormLabel component="legend">{label}</FormLabel>
          {popover && <PopOverInfo {...popover} />}
        </Box>
        <FormGroup>
          {checkboxes &&
            checkboxes.map((checkbox, index) => (
              <FormControlLabel
                sx={styles.formControlLabel}
                key={checkbox.id + index}
                control={
                  <Checkbox
                    {...props}
                    {...field}
                    // checked={field.value?.some((checked: string) => checked === checkbox.value)}
                    onChange={() => handleChange(checkbox.value ?? '')}
                  />
                }
                label={checkbox.label}
              />
            ))}
        </FormGroup>
        <FormHelperText error={hasError} variant="standard" sx={styles.errorLabel}>
          {required && hasError ? (
            <ErrorMessage>
              <>{fieldState.error?.message}</>
            </ErrorMessage>
          ) : (
            assistiveText
          )}
        </FormHelperText>
      </FormControl>
    </div>
  )
}
